import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CheckCircle, Calendar, Clock, Hash, DollarSign, AlertCircle, Video, Send } from "lucide-react";
import config from "../config";
import { PRACTICE, lateCancelFeeText } from "../data/practice";
import { STORAGE_KEY_BOOKING } from "../hooks/useBooking";
import { formatBusinessDate, formatBusinessTime, isIsoWithOffset } from "../services/utils/dateUtils";
import { safeHttpsUrl } from "../services/utils/safeUrl";

/** ?status= values that mean the online payment did not go through. */
const PAYMENT_NOT_COMPLETED = {
  failed: {
    title: "Payment Not Completed",
    message: "Your online payment did not go through and no payment was taken.",
  },
  cancelled: {
    title: "Payment Cancelled",
    message: "Your online payment was cancelled and no payment was taken.",
  },
};
PAYMENT_NOT_COMPLETED.cancel = PAYMENT_NOT_COMPLETED.cancelled;

// "$150", "$150.50", "$25 BSD"
function formatAmount(amount, currency) {
  const n = Number(amount) || 0;
  const value = `$${Number.isInteger(n) ? n : n.toFixed(2)}`;
  return currency && currency !== "USD" ? `${value} ${currency}` : value;
}

// Only https: join links are rendered. React 18 does not block javascript:
// hrefs, and this value comes from sessionStorage, so a bad value must not
// become a clickable script URL.
const safeMeetingUrl = safeHttpsUrl;

function clearStoredBooking() {
  try {
    sessionStorage.removeItem(STORAGE_KEY_BOOKING);
  } catch (error) {
    // Storage unavailable: nothing to clear.
  }
}

/**
 * The PII-free summary written by useBooking, or null.
 * Legacy shapes (no startISO) and bookings whose session has already ended
 * are dropped, so a later visit in the same tab does not show an old booking.
 * @returns {{bookingReference:string, serviceName:string, startISO:string, endISO:string, amount:number, currency:string, paymentStatus:string, status?:string, meetingUrl:string|null}|null}
 */
function readStoredBooking() {
  let booking = null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY_BOOKING);
    booking = raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
  if (!booking || !booking.bookingReference || !isIsoWithOffset(booking.startISO)) {
    return null;
  }
  const endsAt = Date.parse(isIsoWithOffset(booking.endISO) ? booking.endISO : booking.startISO);
  if (endsAt < Date.now()) {
    clearStoredBooking();
    return null;
  }
  return { ...booking, meetingUrl: safeMeetingUrl(booking.meetingUrl) };
}

const rowStyle = { display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' };

function CancellationPolicy({ contactEmail }) {
  return (
    <div style={{ backgroundColor: '#fff3cd', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
      <strong>Cancellation Policy:</strong> Sessions must be cancelled or rescheduled at
      least {PRACTICE.cancellationWindowHours} hours in advance. Late cancellations and
      no-shows may be charged {lateCancelFeeText()}. To cancel or reschedule, email{' '}
      <a href={`mailto:${contactEmail}`} className="text-link">{contactEmail}</a> with your
      booking reference.
    </div>
  );
}

/**
 * The page for a booking request (status 'scheduled', contract v1.3 §3.4):
 * received, not yet confirmed. Never shows a join link: it arrives with
 * the confirmation email.
 */
function RequestReceived({ booking, amount, isConsultation, contactEmail, onLeave }) {
  return (
    <section className="section white">
      <div className="container" style={{ padding: '3rem 0' }}>
        <div className="card" style={{ maxWidth: '700px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <Send size={72} color="#514275" style={{ marginBottom: '1rem' }} aria-hidden="true" />
            <h1 style={{ marginBottom: '0.5rem', color: '#514275' }}>Request Received</h1>
            <p className="muted">
              Thank you. Your request has been sent to Jasmine, and this time is held for you
              while she reviews it. Your booking is not confirmed yet.
            </p>
          </div>

          <div style={{ backgroundColor: '#f5effa', padding: '1.5rem', borderRadius: '8px', marginBottom: '1.5rem' }}>
            <h2 style={{ marginBottom: '1rem', fontSize: '1.3rem' }}>Your Request</h2>

            <div style={rowStyle}>
              <Hash size={20} color="#514275" aria-hidden="true" />
              <div>
                <strong>Booking reference:</strong> {booking.bookingReference}
              </div>
            </div>

            <div style={rowStyle}>
              <CheckCircle size={20} color="#514275" aria-hidden="true" />
              <div>
                <strong>Service:</strong> {booking.serviceName || 'N/A'}
              </div>
            </div>

            <div style={rowStyle}>
              <Calendar size={20} color="#514275" aria-hidden="true" />
              <div>
                <strong>Date:</strong> {formatBusinessDate(booking.startISO)}
              </div>
            </div>

            <div style={{ ...rowStyle, marginBottom: isConsultation ? 0 : rowStyle.marginBottom }}>
              <Clock size={20} color="#514275" aria-hidden="true" />
              <div>
                <strong>Time:</strong> {formatBusinessTime(booking.startISO)}
              </div>
            </div>

            {!isConsultation && (
              <div style={{ ...rowStyle, marginBottom: 0 }}>
                <DollarSign size={20} color="#514275" aria-hidden="true" />
                <div>
                  <strong>Session fee:</strong> {formatAmount(amount, booking.currency)}
                </div>
              </div>
            )}
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.3rem' }}>What Happens Next</h2>
            <ol style={{ paddingLeft: '1.5rem', lineHeight: '1.8' }}>
              <li>You&apos;ll get an email saying your request was received.</li>
              <li>
                Jasmine confirms each booking herself. When she confirms yours, you&apos;ll get a
                confirmation email with your {PRACTICE.meetingPlatform} video link and a calendar
                invite (.ics file).
              </li>
              <li>
                If she can&apos;t make this time, she&apos;ll email you so you can choose another.
              </li>
              {!isConsultation && (
                <li>
                  No payment is taken online. Pay by bank transfer or cheque before or at your
                  session. For the bank details, email{' '}
                  <a href={`mailto:${contactEmail}`} className="text-link">{contactEmail}</a>.
                </li>
              )}
            </ol>
            <p className="muted" style={{ fontSize: '0.9rem' }}>
              Please check your spam folder if you don&apos;t see my emails.
            </p>
          </div>

          <CancellationPolicy contactEmail={contactEmail} />

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button type="button" className="btn-cta" onClick={() => onLeave('/checkout')}>
              Request Another Session
            </button>
            <button
              type="button"
              className="btn-cta"
              onClick={() => onLeave('/')}
              style={{ backgroundColor: '#7d6a91' }}
            >
              Return Home
            </button>
          </div>

          <div style={{ textAlign: 'center', marginTop: '2rem', fontSize: '0.875rem', color: '#7d6a91' }}>
            Questions? Email me at{' '}
            <a href={`mailto:${contactEmail}`} style={{ color: '#514275', textDecoration: 'underline' }}>
              {contactEmail}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * BookingConfirmation Page
 *
 * Shows the booking just made in this tab, from the summary useBooking keeps
 * in sessionStorage ('current_booking'). Dates and times are shown in the
 * business zone (Bahamas time) from the stored UTC instants.
 *
 * Contract v1.3: a new booking is a request (status 'scheduled') that the
 * owner confirms by email, so the page says the request was received and
 * what happens next, with no join link. A 'confirmed' booking (an older
 * backend, or a summary stored without a status) keeps the confirmed page.
 *
 * Query parameters:
 * - status: 'failed' | 'cancelled' (or 'cancel') when returning from an
 *   online payment that did not complete. Only used when payments are on.
 */
export default function BookingConfirmation() {
  const navigate = useNavigate();
  const location = useLocation();
  const contactEmail = config.business.contactEmail;

  // Read once per visit; the booking does not change while the page is open.
  const [booking] = useState(readStoredBooking);
  const status = new URLSearchParams(location.search).get('status');
  const notCompleted = PAYMENT_NOT_COMPLETED[status] || null;

  const leave = (to) => {
    clearStoredBooking();
    navigate(to);
  };

  // No booking data found
  if (!booking) {
    return (
      <section className="section white">
        <div className="container" style={{ textAlign: 'center', padding: '4rem 0' }}>
          <div className="card" style={{ maxWidth: '600px', margin: '0 auto' }}>
            <AlertCircle size={64} color="#f57c00" style={{ marginBottom: '1rem' }} aria-hidden="true" />
            <h1>No Booking Found</h1>
            <p className="muted">
              I couldn't find your booking details. This may happen if you navigated here directly
              or your session expired. If you booked, your confirmation email has all the details.
            </p>
            <button
              type="button"
              className="btn-cta"
              onClick={() => navigate('/checkout')}
              style={{ marginTop: '1.5rem' }}
            >
              Create a New Booking
            </button>
          </div>
        </div>
      </section>
    );
  }

  // Online payment did not complete (returned from the payment page)
  if (notCompleted) {
    return (
      <section className="section white">
        <div className="container" style={{ textAlign: 'center', padding: '4rem 0' }}>
          <div className="card" style={{ maxWidth: '600px', margin: '0 auto' }}>
            <AlertCircle size={64} color="#d32f2f" style={{ marginBottom: '1rem' }} aria-hidden="true" />
            <h1>{notCompleted.title}</h1>
            <p className="muted">{notCompleted.message}</p>
            <p>
              Your booking reference is <strong>{booking.bookingReference}</strong> for{' '}
              {formatBusinessDate(booking.startISO)} at {formatBusinessTime(booking.startISO)}.
              Please email me at{' '}
              <a href={`mailto:${contactEmail}`} className="text-link">{contactEmail}</a>{' '}
              to arrange payment or to cancel.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '1.5rem', flexWrap: 'wrap' }}>
              <button type="button" className="btn-cta" onClick={() => leave('/')}>
                Return Home
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const amount = Number(booking.amount) || 0;
  const isConsultation = amount === 0;

  if (booking.status === 'scheduled') {
    return (
      <RequestReceived
        booking={booking}
        amount={amount}
        isConsultation={isConsultation}
        contactEmail={contactEmail}
        onLeave={leave}
      />
    );
  }

  const isPaid = booking.paymentStatus === 'paid';
  const isPaymentPending = booking.paymentStatus === 'pending';

  let headline;
  if (isConsultation) {
    headline = "Your free consultation has been scheduled.";
  } else if (isPaid) {
    headline = "Payment successful. Your session has been booked.";
  } else if (isPaymentPending) {
    headline = "Your session has been booked. Your payment is being confirmed.";
  } else {
    headline = "Your session has been booked. Payment is taken at your session.";
  }

  let amountLabel = null;
  if (!isConsultation) {
    if (isPaid) amountLabel = "Amount paid";
    else if (isPaymentPending) amountLabel = "Amount (payment processing)";
    else amountLabel = "Amount due at session";
  }

  return (
    <section className="section white">
      <div className="container" style={{ padding: '3rem 0' }}>
        <div className="card" style={{ maxWidth: '700px', margin: '0 auto' }}>
          {/* Success Icon */}
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <CheckCircle size={80} color="#4caf50" style={{ marginBottom: '1rem' }} aria-hidden="true" />
            <h1 style={{ marginBottom: '0.5rem', color: '#2e7d32' }}>
              Booking Confirmed!
            </h1>
            <p className="muted">{headline}</p>
          </div>

          {/* Booking Details */}
          <div style={{ backgroundColor: '#f5effa', padding: '1.5rem', borderRadius: '8px', marginBottom: '1.5rem' }}>
            <h2 style={{ marginBottom: '1rem', fontSize: '1.3rem' }}>Booking Details</h2>

            <div style={rowStyle}>
              <Hash size={20} color="#514275" aria-hidden="true" />
              <div>
                <strong>Booking reference:</strong> {booking.bookingReference}
              </div>
            </div>

            <div style={rowStyle}>
              <CheckCircle size={20} color="#514275" aria-hidden="true" />
              <div>
                <strong>Service:</strong> {booking.serviceName || 'N/A'}
              </div>
            </div>

            <div style={rowStyle}>
              <Calendar size={20} color="#514275" aria-hidden="true" />
              <div>
                <strong>Date:</strong> {formatBusinessDate(booking.startISO)}
              </div>
            </div>

            <div style={rowStyle}>
              <Clock size={20} color="#514275" aria-hidden="true" />
              <div>
                <strong>Time:</strong> {formatBusinessTime(booking.startISO)}
              </div>
            </div>

            {booking.meetingUrl && (
              <div style={rowStyle}>
                <Video size={20} color="#514275" aria-hidden="true" />
                <div>
                  <strong>Join link:</strong>{' '}
                  <a href={booking.meetingUrl} target="_blank" rel="noopener noreferrer" className="text-link">
                    {PRACTICE.meetingPlatform} session link
                  </a>
                </div>
              </div>
            )}

            {amountLabel && (
              <div style={{ ...rowStyle, marginBottom: 0 }}>
                <DollarSign size={20} color="#514275" aria-hidden="true" />
                <div>
                  <strong>{amountLabel}:</strong> {formatAmount(amount, booking.currency)}
                </div>
              </div>
            )}
          </div>

          {/* Next Steps */}
          <div style={{ marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.3rem' }}>What's Next?</h2>
            <ul style={{ paddingLeft: '1.5rem', lineHeight: '1.8' }}>
              <li>A confirmation email is on its way to the address you entered.</li>
              <li>
                The email and its calendar invite (.ics file) carry your {PRACTICE.meetingPlatform} join
                link and session details. If the link isn't ready yet, I'll send it before your session.
              </li>
              <li>Please check your spam folder if you don't see it within 10 minutes.</li>
              {!isConsultation && !isPaid && !isPaymentPending && (
                <li>Payment is taken at your session. No payment has been taken online.</li>
              )}
            </ul>
          </div>

          <CancellationPolicy contactEmail={contactEmail} />

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button type="button" className="btn-cta" onClick={() => leave('/checkout')}>
              Book Another Session
            </button>
            <button
              type="button"
              className="btn-cta"
              onClick={() => leave('/')}
              style={{ backgroundColor: '#7d6a91' }}
            >
              Return Home
            </button>
          </div>

          {/* Contact Support */}
          <div style={{ textAlign: 'center', marginTop: '2rem', fontSize: '0.875rem', color: '#7d6a91' }}>
            Questions? Email me at{' '}
            <a href={`mailto:${contactEmail}`} style={{ color: '#514275', textDecoration: 'underline' }}>
              {contactEmail}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
